/**
 * ConfirmModal - A reusable confirmation modal for various actions
 * 
 * Features:
 * - Custom styling matching FinList design system
 * - Supports multiple variants: danger, warning, info, neutral
 * - Animated with framer-motion
 * - Keyboard accessible (Escape to cancel, Enter to confirm)
 * - Loading state support
 * - Promise-based API via useConfirmDialog hook
 * 
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: () => void - Called when modal is closed/cancelled
 * - onConfirm: () => void - Called when action is confirmed
 * - title?: string - Modal title (default: "Confirm")
 * - message?: string - Confirmation message
 * - itemName?: string - Name of the item (shown in message)
 * - confirmText?: string - Confirm button text (default: "Confirm")
 * - cancelText?: string - Cancel button text (default: "Cancel")
 * - variant?: 'danger' | 'warning' | 'info' | 'neutral' - Color variant
 * - loading?: boolean - Show loading state on confirm button
 * - icon?: ReactNode - Custom icon (optional, uses default based on variant)
 */

import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Trash2, Info, HelpCircle, Archive, X } from 'lucide-react';

// Variant configurations
const VARIANTS = {
  danger: {
    icon: Trash2,
    iconBg: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    buttonText: 'text-white'
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    button: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
    buttonText: 'text-white'
  },
  info: {
    icon: Info,
    iconBg: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    button: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    buttonText: 'text-white'
  },
  neutral: {
    icon: HelpCircle,
    iconBg: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    button: 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500',
    buttonText: 'text-white'
  },
  archive: {
    icon: Archive,
    iconBg: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    button: 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500',
    buttonText: 'text-white'
  }
};

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm',
  message,
  itemName,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'neutral',
  loading = false,
  icon: CustomIcon
}) {
  // Handle keyboard events
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const variantConfig = VARIANTS[variant] || VARIANTS.neutral;
  const IconComponent = CustomIcon || variantConfig.icon;

  // Build the message
  const displayMessage = message || (
    itemName 
      ? `Are you sure you want to proceed with "${itemName}"? This action cannot be undone.`
      : 'Are you sure you want to proceed? This action cannot be undone.'
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', duration: 0.3, bounce: 0.2 }}
            className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
            aria-describedby="confirm-modal-description"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className={`p-3 rounded-full ${variantConfig.iconBg}`}>
                  <IconComponent className="w-8 h-8" />
                </div>
              </div>

              {/* Title */}
              <h2
                id="confirm-modal-title"
                className="text-xl font-bold text-center text-gray-900 dark:text-white mb-2"
              >
                {title}
              </h2>

              {/* Message */}
              <p
                id="confirm-modal-description"
                className="text-center text-gray-600 dark:text-gray-300 mb-6"
              >
                {displayMessage}
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50"
                >
                  {cancelText}
                </button>
                <button
                  onClick={onConfirm}
                  disabled={loading}
                  className={`flex-1 px-4 py-2.5 rounded-xl font-medium ${variantConfig.button} ${variantConfig.buttonText} transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center gap-2`}
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <span>{confirmText}</span>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Named export for backward compatibility with existing imports
export const ConfirmDeleteModal = ConfirmModal;
